import { parseLocalDate } from './dates';

type TaskType = 'design' | 'development' | 'integration' | 'code_review' | 'qa';

export interface DependencyTask {
    id: number;
    project_id: number;
    type: TaskType;
    start_date: string;
    end_date: string;
}

export interface DependencyValidationResult {
    valid: boolean;
    error?: string;
    suggestion?: string;
}

interface DependencyRule {
    mustStartAfter?: TaskType[];
    mustFinishBefore?: TaskType[];
}

export const DEPENDENCY_RULES: Partial<Record<TaskType, DependencyRule>> = {
    development: {
        mustStartAfter: ['design'],
    },
    integration: {
        mustStartAfter: ['development'],
    },
    code_review: {
        mustFinishBefore: ['qa'],
    },
    qa: {
        mustStartAfter: ['code_review', 'integration'],
    },
};

const TASK_TYPE_LABELS: Record<TaskType, string> = {
    design: 'Design',
    development: 'Development',
    integration: 'Integration',
    code_review: 'Code Review',
    qa: 'QA',
};

/**
 * Validate a task's dates against dependency rules given all sibling tasks in the project.
 * Returns first violation found, or { valid: true } if no rules are violated.
 * Pure function — no side effects.
 */
export function validateTaskDependencies(
    task: DependencyTask,
    allTasks: DependencyTask[]
): DependencyValidationResult {
    const rules = DEPENDENCY_RULES[task.type];
    if (!rules) return { valid: true };

    const siblings = (type: TaskType) =>
        allTasks.filter((t) => t.project_id === task.project_id && t.type === type && t.id !== task.id);

    const taskStart = parseLocalDate(task.start_date);

    for (const predecessorType of rules.mustStartAfter ?? []) {
        // Use the latest-ending predecessor — the worst-case constraint.
        const candidates = siblings(predecessorType);
        if (candidates.length === 0) continue;
        const predecessor = candidates.reduce((latest, t) =>
            parseLocalDate(t.end_date) > parseLocalDate(latest.end_date) ? t : latest
        );

        const predecessorEnd = parseLocalDate(predecessor.end_date);
        if (taskStart <= predecessorEnd) {
            const label = TASK_TYPE_LABELS[task.type];
            const predLabel = TASK_TYPE_LABELS[predecessorType];
            return {
                valid: false,
                error: `${label} must start after ${predLabel} finishes.`,
                suggestion: `Move ${label} task to start after ${predLabel} task ends (${predecessor.end_date}).`,
            };
        }
    }

    const taskEnd = parseLocalDate(task.end_date);

    for (const successorType of rules.mustFinishBefore ?? []) {
        // Use the earliest-starting successor — the worst-case constraint.
        const candidates = siblings(successorType);
        if (candidates.length === 0) continue;
        const successor = candidates.reduce((earliest, t) =>
            parseLocalDate(t.start_date) < parseLocalDate(earliest.start_date) ? t : earliest
        );

        const successorStart = parseLocalDate(successor.start_date);
        if (taskEnd >= successorStart) {
            const label = TASK_TYPE_LABELS[task.type];
            const succLabel = TASK_TYPE_LABELS[successorType];
            return {
                valid: false,
                error: `${label} must finish before ${succLabel} starts.`,
                suggestion: `Move ${label} task to end before ${succLabel} task starts (${successor.start_date}).`,
            };
        }
    }

    return { valid: true };
}
